import { useEffect, useRef, useState } from "react";
import { useCourseDetailStore } from "@/stores/courseDetailStore";
import { createLearningProgress } from "@/api/generated/learning-progresses/learning-progresses";

/**
 * Tracks inline Lernkarten flips and posts LearningProgress completed once
 * every card has been shown on the back at least once in this mount.
 */
const useFlashcardsFlipCompletion = (
  contentElementId: string,
  cardCount: number
) => {
  const [flippedByIndex, setFlippedByIndex] = useState<Record<number, boolean>>(
    {}
  );
  const [seenBackByIndex, setSeenBackByIndex] = useState<
    Record<number, boolean>
  >({});
  const {
    enrollmentId,
    trainerMode,
    refreshProgress,
    completedContentElementIds,
  } = useCourseDetailStore();

  const alreadyCompleted = completedContentElementIds.has(contentElementId);
  const completionPostedRef = useRef(false);

  const toggleCardFlip = (index: number) => {
    const willShowBack = !flippedByIndex[index];
    setFlippedByIndex((current) => ({
      ...current,
      [index]: !current[index],
    }));
    if (!willShowBack) {
      return;
    }
    setSeenBackByIndex((seen) => {
      if (seen[index]) {
        return seen;
      }
      return { ...seen, [index]: true };
    });
  };

  const rememberServerCompletedDeck = () => {
    if (alreadyCompleted) {
      completionPostedRef.current = true;
    }
  };
  useEffect(rememberServerCompletedDeck, [alreadyCompleted]);

  const postProgressWhenEveryCardWasFlipped = () => {
    const everyCardSeen =
      cardCount > 0 &&
      Array.from({ length: cardCount }, (_, index) =>
        Boolean(seenBackByIndex[index])
      ).every(Boolean);

    if (
      !enrollmentId ||
      trainerMode ||
      !everyCardSeen ||
      completionPostedRef.current ||
      alreadyCompleted
    ) {
      return;
    }

    completionPostedRef.current = true;
    void createLearningProgress(enrollmentId, {
      learningProgress: { contentElementId, status: "completed" },
    })
      .then(() => refreshProgress())
      .catch(() => {
        completionPostedRef.current = false;
      });
  };
  useEffect(postProgressWhenEveryCardWasFlipped, [
    alreadyCompleted,
    cardCount,
    contentElementId,
    enrollmentId,
    refreshProgress,
    seenBackByIndex,
    trainerMode,
  ]);

  return { flippedByIndex, toggleCardFlip };
};

export { useFlashcardsFlipCompletion };
